/**
 * @module ol-ishare/ui/gazetteer
 */

/**
 * @typedef {Object} GazetteerOptions
 * @property {String} target DOM element that the control will be created
 * within
 * @property {String} iShareUrl Base URL for iShare Web app. If
 * `iShareUrl` and `profile` options are not supplied then one or more {@link
 * module:ol-ishare/ui/gazetteer~GazetteerSource} instances must be specified
 * via the `sources` option.
 * @property {String} profile Name of profile that the search is
 * associated with. If `iShareUrl` and `profile` options are not supplied then
 * one or more {@link module:ol-ishare/ui/gazetteer~GazetteerSource} instances
 * must be specified via the `sources` option.
 * @property {Array<module:ol-ishare/ui/gazetteer~GazetteerSource>}
 * sources List of GazetteerSource instances that will be queried for
 * results. See {@link module:ol-ishare/search~AddressSearch} and {@link
 * module:ol-ishare/search~LayerSearch} for examples of classes that implement
 * GazetteerSource
 * @property {String} placeholder Placeholder text displayed by the input
 * @property {String} noResults Message to display when no results are
 * found
 * @property {String} queryTooShort Accessibility hint to indicate that
 * the query is too short. The string can contain an optional placeholder for
 * `minQueryLength`, for example: `Input ${minQueryLength} or more characters
 * for results`
 * @property {String} selectedOption Accessibility hint to indicate the
 * selected option. The string can contain optional placeholders for
 * `selectedOption`, `length` and `index`, for example: `${selectedOption}
 * ${index} of ${length} is highlighted`
 * @property {String} resultsDescription Accessibility hint to indicate
 * which options are available and which is selected. The string can contain
 * optional placeholders for `length` and `selectedOption`, for example:
 * `Result count: ${length}. ${selectedOption}`
 * @property {String} assistiveHint Accessibility hint to provide an
 * initial instruction to the assistive tech user.
 */

// TODO Should GazetteerSource instances provide some info such as:
//      * Suggested resolution for zooming to coord
//      * Name of the ID column
//      Could be added to GazetteerResult object via a `metadata` property?

// TODO It might be nice if there was an easy way to access either the full
//      feature or at least the attributes for a LayerSearch GazetteerResult object
//      Could be added to GazetteerResult object via an optional `detail` property?

/**
 * Interface for GazetteerSource instances.
 * @interface GazetteerSource
 */

/**
 * Search for results based on the provided query string, returns a Promise which will resolve to
 * an Array of {@link module:ol-ishare/ui/gazetteer~GazetteerResult} objects or an error
 *
 * @function
 * @name module:ol-ishare/ui/gazetteer~GazetteerSource#search
 * @param {string} query String to search for
 * @returns {Promise<Array<module:ol-ishare/ui/gazetteer~GazetteerResult>>} Promise that resolves to an Array of GazetteerResult objects or an Error
 */

/**
 * @typedef GazetteerResult
 * @property {string} id Unique ID of the result (UPRN, planning application reference etc.)
 * @property {Array<number>} coord Coordinate of result e.g. `[x: number, y: number]`
 * @property {Array<number>} bbox Bounding box coordinates of the result e.g. `[x_min: number, y_min: number, x_max: number, y_max: number]`
 * @property {string} srs Spatial reference system code of `coord` or `bbox`
 * @property {string} text Result text
 * @property {string} formatted Result text with matching part of text highlighted via html
 */

import Observable from 'ol/Observable';
import accessibleAutocomplete from 'accessible-autocomplete';
import { AddressSearch } from '../search.js';
import { getLang, lang } from '../lang.js';
import { template } from '../string.js';

/**
 * Autocomplete Gazetteer search
 * @class
 * @classdesc Standalone auto-complete search
 *
 * If any of the `placeholder`, `noResults`, `queryTooShort`, `selectedOption`,
 * `resultsDescription` or `assistiveHint` options are unset, the iShare server
 * version is >= v6.0.9, your langauge file contains matching properties under
 * `oli.gazetteer` and the lang UI text lookup is available when this control
 * is created then the entries from the language file will be used. Otherwise
 * the default English values will be used. The lang lookup can be loaded by
 * calling `ol-ishare/lang~getLang(iShareUrl)` which returns a Promise that
 * resolves to the `lang` lookup object.
 *
 * @param {module:ol-ishare/ui/gazetteer~GazetteerOptions} opt_options Gazetteer options
 */
var Gazetteer = (function (_super) {
  function Gazetteer(opt_options) {
    _super.call(this);
    var options = opt_options || {};

    // Increament instance count to allow us to generate unique id's for elements
    Gazetteer._instanceCount += 1;

    if (options.target && options.target.nodeType !== undefined) {
      // We have an element
      this.element = options.target;
    } else {
      // Assume we've been passed an ID, get the element by ID
      this.element = document.getElementById(options.target);
    }
    this.element.classList.add('oli-gazetteer');

    // Generate an ID for the input
    this.inputId = 'oli-gazetteer-' + Gazetteer._instanceCount;

    this.sources = options.sources || [];

    if (this.sources.length === 0) {
      // Default to creating an addresss search if sources are not
      // specified and iShareUrl and profile are
      if (options.iShareUrl && options.profile) {
        this.sources.push(
          new AddressSearch({
            iShareUrl: options.iShareUrl,
            profile: options.profile
          })
        );
      } else {
        console.warn(
          'Gazetteer - No sources specified and iShareUrl and profile options missing...'
        );
      }
    }

    this.requestCounter = 0;
    this.handleConfirm = this.handleConfirm.bind(this);
    this.search = this.search.bind(this);

    function inputValueTemplate(result) {
      return result && result.text;
    }

    function suggestionTemplate(result) {
      return result && (result.formatted || result.text);
    }

    /**
     * Returns a function, that, as long as it continues to be invoked, will not
     * be triggered. The function will be called after it stops being called for
     * N milliseconds. If `immediate` is passed, trigger the function on the
     * leading edge, instead of the trailing.
     * Copied from https://davidwalsh.name/javascript-debounce-function
     * @param {Function} func Function to debounce
     * @param {Number} wait ms to wait
     * @param {Boolean} immediate trigger the function on the leading edge
     * @returns {undefined}
     * @private
     */
    function debounce(func, wait, immediate) {
      var timeout;
      return function () {
        var context = this,
          args = arguments;
        var later = function () {
          timeout = null;
          if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
      };
    }

    const gazOpts = {
      element: this.element,
      id: this.inputId,
      source: debounce(this.search, 500, false),
      asyncResults: true,
      autoselect: false,
      confirmOnBlur: false,
      minLength: 2,
      onConfirm: this.handleConfirm,
      templates: {
        inputValue: inputValueTemplate,
        suggestion: suggestionTemplate
      }
    };

    // Only specify functions for the UI text options if provided
    options.placeholder =
      options.placeholder ?? lang?.oli?.gazetteer?.placeholder;
    // Provide a default "Partial address or postcode..." placeholder if
    // placeholder has not been set via options.placeholder or the language
    // file and the only search source is an AddressSearch
    if (options.placeholder === undefined) {
      if (
        this.sources.length === 1 &&
        this.sources[0] instanceof AddressSearch
      ) {
        options.placeholder = 'Partial address or postcode...';
      }
    }
    if (options.placeholder) {
      gazOpts.placeholder = options.placeholder;
    }
    options.noResults = options.noResults ?? lang?.oli?.gazetteer?.noResults;
    if (options.noResults) {
      gazOpts.tNoResults = () => options.noResults;
      gazOpts.tStatusNoResults = () => options.noResults;
    }
    options.assistiveHint =
      options.assistiveHint ?? lang?.oli?.gazetteer?.assistiveHint;
    if (options.assistiveHint) {
      gazOpts.tAssistiveHint = () => options.assistiveHint;
    }
    options.queryTooShort =
      options.queryTooShort ?? lang?.oli?.gazetteer?.queryTooShort;
    if (options.queryTooShort) {
      gazOpts.tStatusQueryTooShort = (minQueryLength) => {
        return template(options.queryTooShort, { minQueryLength });
      };
    }
    options.selectedOption =
      options.selectedOption ?? lang?.oli?.gazetteer?.selectedOption;
    if (options.selectedOption) {
      gazOpts.tStatusSelectedOption = (selectedOption, length, idx) => {
        const index = idx + 1;
        return template(options.selectedOption, {
          selectedOption,
          length,
          index
        });
      };
    }
    options.resultsDescription =
      options.resultsDescription ?? lang?.oli?.gazetteer?.resultsDescription;
    if (options.resultsDescription) {
      gazOpts.tStatusResults = (length, selectedOption) => {
        return template(options.resultsDescription, { length, selectedOption });
      };
    }

    accessibleAutocomplete(gazOpts);
  }

  if (_super) Gazetteer.__proto__ = _super;
  Gazetteer.prototype = Object.create(_super && _super.prototype);
  Gazetteer.prototype.constructor = Gazetteer;

  Gazetteer.prototype.handleConfirm = function handleConfirm(result) {
    if (result) {
      this.dispatchEvent({ type: 'select', result: result });
      var inputId = this.inputId;
      setTimeout(function () {
        document.getElementById(inputId).blur();
      }, 0);
    }
  };

  Gazetteer.prototype.search = function search(query, populateResults) {
    this.requestCounter += 1;
    var requestNum = this.requestCounter;
    var self = this;
    var ps = this.sources.map(function (source) {
      return source.search(query);
    });
    Promise.allSettled(ps)
      .then(function (results) {
        // If the current requestNum doesn't match then
        // we simply skip it as it's been superseeded by
        // a more recent request
        if (requestNum === self.requestCounter) {
          // Skip rejected promises and flattern results into a single Array
          results = results
            .filter(function (p, idx) {
              if (p.status === 'rejected') {
                console.warn(
                  `Error getting results for query "${query}" from source:`,
                  self.sources[idx],
                  '\n\nReason:\n',
                  p.reason
                );
                return false;
              }
              return true;
            })
            .reduce(function (acc, p) {
              return acc.concat(p.value);
            }, []);
          populateResults(results);
        }
      })
      .catch(function (err) {
        if (requestNum === self.requestCounter) {
          populateResults([]);
        }
      });
  };

  Gazetteer._instanceCount = 0;

  return Gazetteer;
})(Observable);

/**
 * Factory function to create a UI Gazetteer instance pre-configured with UI
 * text loaded from iShare Language Editor
 * @param {String} iShareUrl Base URL for iShare Web app
 * @param {module:ol-ishare/ui/gazetteer~GazetteerOptions} options Gazetteer options
 * @returns {Promise<module:ol-ishare/ui/gazetteer~Gazetteer>} Promise which resolves to a UI Gazetteer instance
 */
function createGazetteer(iShareUrl, options) {
  return getLang(iShareUrl).then((lang) => {
    return new Gazetteer(options);
  });
}

export { createGazetteer, Gazetteer };
